import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Paper
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import { API_BASE_URL } from 'src/apiConfig';
import { exportPlacanjaToExcel } from './helper/excelExport';

export default function SchoolPay() {
  const [schools, setSchools] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalStudentCount, setTotalStudentCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEdit, setCurrentEdit] = useState({
    id: null,
    id_skole: null,
    school_name: '',
    broj_racuna: '',
    datum_placanja: '',
    broj_izvoda: '',
    napomena: ''
  });
  const [errors, setErrors] = useState({});
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [schRes, payRes, countRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/schoolPay.php?action=getSchools`),
          axios.get(`${API_BASE_URL}/api/schoolPay.php?action=getAllPayments`),
          axios.get(`${API_BASE_URL}/api/students.php?student_count`)
        ]);
        const schData = schRes.data.map((s) => ({
          ...s,
          student_count: Number(s.student_count)
        }));
        setSchools(schData);
        setPayments(payRes.data);
        setTotalStudentCount(Number(countRes.data.count));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  const handleSave = async () => {
    const hasAnyValue =
      currentEdit.broj_racuna ||
      currentEdit.datum_placanja ||
      currentEdit.broj_izvoda ||
      currentEdit.napomena;
    if (!hasAnyValue) {
      setErrors({ datum_placanja: 'Bar jedno polje mora biti popunjeno.' });
      return;
    }

    const payload = {
      id_skole: currentEdit.id_skole,
      broj_racuna: currentEdit.broj_racuna,
      datum_placanja: currentEdit.datum_placanja,
      broj_izvoda: currentEdit.broj_izvoda,
      napomena: currentEdit.napomena
    };

    try {
      if (currentEdit.id) {
        await axios.put(
          `${API_BASE_URL}/api/schoolPay.php?id=${currentEdit.id}`,
          payload
        );
      } else {
        await axios.post(`${API_BASE_URL}/api/schoolPay.php`, payload);
      }
      const payRes = await axios.get(
        `${API_BASE_URL}/api/schoolPay.php?action=getAllPayments`
      );
      setPayments(payRes.data);
      setIsModalOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_BASE_URL}/api/schoolPay.php?id=${deleteId}`);
      const payRes = await axios.get(
        `${API_BASE_URL}/api/schoolPay.php?action=getAllPayments`
      );
      setPayments(payRes.data);
      setIsConfirmOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const rows = schools.map((sch) => {
    const pay = payments.find(
      (p) => Number(p.id_skole) === Number(sch.school_id)
    );
    const isPopulated = !!(
      pay?.broj_racuna ||
      pay?.datum_placanja ||
      pay?.broj_izvoda ||
      pay?.napomena
    );
    return {
      ...sch,
      payment_exists: isPopulated,
      id_placanja: pay?.id || null,
      broj_racuna: pay?.broj_racuna || '',
      datum_placanja: pay?.datum_placanja || '',
      broj_izvoda: pay?.broj_izvoda || '',
      napomena: pay?.napomena || ''
    };
  });

  const filteredRows = rows.filter((row) =>
    `${row.school_name} ${row.email} ${row.location_name} ${row.napomena}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  const columns = [
    {
      field: 'payment_exists',
      headerName: 'Status',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <CheckCircleIcon sx={{ color: 'green' }} />
        ) : (
          <CloseIcon sx={{ color: 'red' }} />
        )
    },
    { field: 'school_name', headerName: 'Škola', width: 200 },
    { field: 'phone', headerName: 'Telefon', width: 150 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'location_name', headerName: 'Lokacija', width: 150 },
    { field: 'student_count', headerName: 'Učenika', width: 120 },
    { field: 'broj_racuna', headerName: 'Broj računa', width: 120 },
    { field: 'datum_placanja', headerName: 'Datum', width: 120 },
    { field: 'broj_izvoda', headerName: 'Broj izvoda', width: 120 },
    { field: 'napomena', headerName: 'Napomena', width: 150 },
    {
      field: 'actions',
      headerName: 'Akcije',
      width: 200,
      renderCell: (params) =>
        params.row.payment_exists ? (
          <>
            <IconButton
              color="info"
              onClick={() => {
                const p = payments.find(
                  (p) => Number(p.id_skole) === Number(params.row.school_id)
                );
                setCurrentEdit({
                  id: p.id,
                  id_skole: params.row.school_id,
                  school_name: params.row.school_name,
                  broj_racuna: p.broj_racuna,
                  datum_placanja: p.datum_placanja,
                  broj_izvoda: p.broj_izvoda,
                  napomena: p.napomena
                });
                setErrors({});
                setIsModalOpen(true);
              }}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => {
                setDeleteId(params.row.id_placanja);
                setIsConfirmOpen(true);
              }}
            >
              <DeleteIcon />
            </IconButton>
          </>
        ) : (
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setCurrentEdit({
                id: null,
                id_skole: params.row.school_id,
                school_name: params.row.school_name,
                broj_racuna: '',
                datum_placanja: '',
                broj_izvoda: '',
                napomena: ''
              });
              setErrors({});
              setIsModalOpen(true);
            }}
          >
            Dodaj
          </Button>
        )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" mb={1}>
            Ukupan broj učenika: {totalStudentCount}
          </Typography>
          <Typography variant="subtitle1">
            Ukupan broj škola: {schools.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Pretraga..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              minWidth: '280px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0px 1px 3px rgba(0,0,0,0.1)'
            }}
            InputProps={{
              sx: { paddingLeft: 1 }
            }}
          />
          <Button
            variant="contained"
            onClick={() => exportPlacanjaToExcel(rows, 'placanja')}
            sx={{
              backgroundColor: '#2596be',
              textTransform: 'none',
              padding: '6px 14px',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 'bold',
              boxShadow: '0px 2px 5px rgba(0,0,0,0.1)',
              '&:hover': {
                backgroundColor: '#1c75bc'
              }
            }}
          >
            Izvezi u Excel
          </Button>
        </Box>
      </Box>
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(r) => r.school_id}
            autoHeight
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableColumnMenu
          />
        )}
      </Paper>
      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 3,
            borderRadius: 2
          }}
        >
          <Typography variant="h6" gutterBottom>
            {currentEdit.id ? 'Izmeni plaćanje' : 'Dodaj plaćanje'} -{' '}
            {currentEdit.school_name}
          </Typography>
          <TextField
            label="Broj računa"
            value={currentEdit.broj_racuna}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, broj_racuna: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Datum plaćanja"
            value={currentEdit.datum_placanja}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, datum_placanja: e.target.value })
            }
            fullWidth
            margin="normal"
            error={Boolean(errors.datum_placanja)}
            helperText={errors.datum_placanja}
          />
          <TextField
            label="Broj izvoda"
            value={currentEdit.broj_izvoda}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, broj_izvoda: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <TextField
            label="Napomena"
            value={currentEdit.napomena}
            onChange={(e) =>
              setCurrentEdit({ ...currentEdit, napomena: e.target.value })
            }
            fullWidth
            margin="normal"
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button onClick={() => setIsModalOpen(false)} sx={{ mr: 1 }}>
              Otkaži
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Sačuvaj
            </Button>
          </Box>
        </Box>
      </Modal>
      <ConfirmDeleteDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
